import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPlatformDashboard } from "@/lib/platformApi";

export default function PlatformHome() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlatformDashboard()
      .then(setDashboard)
      .catch(() => setDashboard(null))
      .finally(() => setLoading(false));
  }, []);

  const metrics = dashboard || {};

  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-[#0F2D5C]">Panel BNT</h2>
        <p className="mt-2 text-sm text-slate-600">
          Flujo diario: solicitudes → validar pago → emitir. Alta comercial y organizaciones quedan en «Más opciones».
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to="/platform/license-requests"
            className="inline-flex items-center rounded-md bg-[#0F2D5C] px-4 py-2 text-sm font-medium text-white hover:bg-[#0a2247] transition-colors"
          >
            Ir a solicitudes
          </Link>
          <Link
            to="/platform/onboarding"
            className="inline-flex items-center rounded-md border border-[#0F2D5C]/30 bg-white px-4 py-2 text-sm font-medium text-[#0F2D5C] hover:bg-[#0F2D5C]/5 transition-colors"
          >
            Alta comercial
          </Link>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            ["Organizaciones", metrics.organizations_total ?? "—"],
            ["Licencias activas", metrics.active_licenses ?? "—"],
            ["Usuarios hoy", metrics.users_active_today ?? "—"],
            ["SAT atrasado", metrics.sat_sync_delayed ?? "—"],
            ["Altas en 24 h", metrics.onboarding_last_24h ?? "—"],
          ].map(([label, count]) => (
            <div
              key={label}
              className="rounded-md border border-[#0F2D5C]/15 p-3"
            >
              <div className="text-xl font-bold text-[#0F2D5C] tabular-nums">{count}</div>
              <div className="text-xs text-slate-600 mt-1">{label}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/organizations" className="text-[#0F2D5C] underline">
            Organizaciones
          </Link>
          <Link to="/platform/licenses" className="text-[#0F2D5C] underline">
            Emitir por plan
          </Link>
          <Link to="/platform/modules" className="text-[#0F2D5C] underline">
            Planes comerciales
          </Link>
          <Link to="/platform/users" className="text-[#0F2D5C] underline">
            Usuarios
          </Link>
        </div>
      </div>

      {!loading && metrics.alerts?.length > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-amber-950">Alertas operativas</h3>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {metrics.alerts.slice(0, 8).map((a, idx) => (
              <li key={`${a.type}-${idx}`}>• {a.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {!loading && metrics.organizations_in_test?.length > 0 ? (
        <div className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
          <h3 className="text-base font-semibold text-[#0F2D5C]">Organizaciones en prueba</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Nombre</th>
                  <th className="py-2 pr-3">RFC</th>
                  <th className="py-2 pr-3">Licencia</th>
                  <th className="py-2 pr-3">Clientes</th>
                  <th className="py-2">Última actividad</th>
                </tr>
              </thead>
              <tbody>
                {metrics.organizations_in_test.map((o) => (
                  <tr key={o.id} className="border-b border-slate-50">
                    <td className="py-2 pr-3">{o.name}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{o.rfc}</td>
                    <td className="py-2 pr-3">{o.license_status}</td>
                    <td className="py-2 pr-3 tabular-nums">{o.client_count}</td>
                    <td className="py-2 text-xs text-slate-600">{o.last_activity || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {!loading && metrics.active_users?.length > 0 ? (
        <div className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
          <h3 className="text-base font-semibold text-[#0F2D5C]">Usuarios activos</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Nombre</th>
                  <th className="py-2 pr-3">Organización</th>
                  <th className="py-2">Último acceso</th>
                </tr>
              </thead>
              <tbody>
                {metrics.active_users.slice(0, 20).map((u) => (
                  <tr key={u.id} className="border-b border-slate-50">
                    <td className="py-2 pr-3">{u.name || u.email}</td>
                    <td className="py-2 pr-3">{u.organization_name}</td>
                    <td className="py-2 text-xs text-slate-600">{u.last_access || "Sin registro"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}
